import {Component, ViewEncapsulation, Input, OnChanges, Output, EventEmitter, AfterViewInit, OnInit, ElementRef, Renderer2, ViewChild} from '@angular/core';
import {UntypedFormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import { WindowService } from 'src/shared/providers/window.service';
import { ValueAccessorBase } from '../default-class/value-accessor';
import { AutocompleteModel } from './autocomplete.model';

@Component({
    selector: 'tidy-auto-complete',
    templateUrl: 'auto-complete.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./auto-complete.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: AutocompleteComponent,
            multi: true,
        }],
    standalone: false
})
export class AutocompleteComponent extends ValueAccessorBase<any> implements OnChanges, AfterViewInit, OnInit {
  @Input() label: string;
  @Input() helperText: string;
  @Input() form: UntypedFormControl;
  @Input() icon: string;
  @Input() public formControl: any;
  @Input() showOnFocus = false;
  @Input() options: Array<AutocompleteModel>;
  @Input() public submitted: boolean;
  @Input() public errorMessage: string;
  @Input() public isToSearchBetweenOptions = false;
  @Input() selected: AutocompleteModel;
  @Input() showGoogleIcon = false;
  @Input() searchFunction: ((searchText: string) => Promise<AutocompleteModel[]>) | null = null;
  @Input() minSearchLength = 2;
  @Input() searchDebounceTime = 300;

  @Output() public action: EventEmitter<any> = new EventEmitter<any>();
  @Output() public itemSelected: EventEmitter<AutocompleteModel> = new EventEmitter<AutocompleteModel>();

  @ViewChild('inputContainer') inputContainer: ElementRef;

  filteredOptions: Observable<AutocompleteModel[]>;
  filteredOptionsList: AutocompleteModel[] = [];
  isFocused  = false;
  inputLength = 0;
  isListeningChangeSearchInput = false;
  panelWidth: number;
  touchUpdate = false;
  isSearching = false;
  searchError = false;
  private searchTimeout: any;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private windowService: WindowService
  ) {
    super();
  }

  ngOnInit() {
    const isSecondaryLayer = localStorage.getItem('isPrimaryLayer') === 'false' && this.windowService.isDesktopRes;
    if (isSecondaryLayer) {
      this.renderer.addClass(this.el.nativeElement, 'secondary-layer');
    }
  }

  ngAfterViewInit() {
    this.updatePanelWidth();
    if(!this.selected || Object.keys(this.selected).length === 0) {
      return;
    }
    this.selectADefaultOption();
  }

  updatePanelWidth() {
    if (this.inputContainer?.nativeElement) {
      this.panelWidth = this.inputContainer.nativeElement.offsetWidth;
    }
  }

  selectADefaultOption() {
    this.choseItem(this.selected);

    this.form.setValue(this.selected.value);
  }

  setFocused(status: boolean) {
    this.isFocused = status;
    if (status) {
      this.updatePanelWidth();
      // Initialize filtered list on focus if using static options
      if (this.isToSearchBetweenOptions && this.options && !this.searchFunction) {
        this.filteredOptionsList = this._filter(this.value || '');
      }
    }
  }

  onFocusOut() {
    setTimeout(() => {
      this.isFocused = false;
      this.touchUpdate = false;
    }, 150);
  }

  choseItem(item: AutocompleteModel) {
    this.value = item.value;
    this.searchError = false;
    this.action.emit(item);
    this.itemSelected.emit(item);
  }

  async onKeyUp(value: string) {
    this.value = value;
    this.inputLength = value?.length || 0;

    // Filter static options when isToSearchBetweenOptions is enabled
    if (this.isToSearchBetweenOptions && this.options && !this.searchFunction) {
      this.filteredOptionsList = this._filter(value);
    }

    if (this.searchFunction) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      if (!value || value.length < this.minSearchLength) {
        this.options = [];
        this.isSearching = false;
        this.searchError = false;
        return;
      }

      this.searchTimeout = setTimeout(async () => {
        await this.performAsyncSearch(value);
      }, this.searchDebounceTime);
    }
  }

  private async performAsyncSearch(searchText: string) {
    if (!this.searchFunction) {
      return;
    }

    this.isSearching = true;
    this.searchError = false;

    try {
      const results = await this.searchFunction(searchText);
      this.options = results || [];
    } catch (error) {
      this.searchError = true;
      this.options = [];
      this.errorMessage = error?.message || 'Search failed. Please try again.';
    } finally {
      this.isSearching = false;
    }
  }

  onKeyDown() {
    this.isFocused = true;
    this.touchUpdate = true;
  }

  ngOnChanges() {
    if(!this.isToSearchBetweenOptions || this.isListeningChangeSearchInput) {
      return;
    }

    // Support form-control based filtering by subscribing to valueChanges
    if (this.form) {
      this.form.valueChanges
        .pipe(startWith(''))
        .subscribe(value => {
          this.filteredOptionsList = this._filter(value);
        });
    }

    this.isListeningChangeSearchInput = true;
  }

  private _filter(value = ''): AutocompleteModel[] {
    if (!this.options) {
      return [];
    }

    if (value.length === 0) {
      return this.options;
    }

    const filterValue = value.toLowerCase().replace(/\s+/g, '');

    return this.options.filter(option => {
      const lowerCaseValue = option.display.toLocaleLowerCase().replace(/\s+/g, '');
      return lowerCaseValue.includes(filterValue);
    });
  }

  getValue() {
    if (!this.options?.length) {
      return this.value;
    }

    return this.options.find((op) => op.value === this.value)?.display || this.value;
  }

  focus(): void {
    const input = this.el.nativeElement.querySelector('input');
    if (input) {
      input.focus();
      this.isFocused = true;
    }
  }
}
